// Single source of truth (frontend) for default widget / embed settings.
//
// These MIRROR the backend reference in Prime-Backend `shop.service.ts` ->
// `activateDefaultWidgets()`, which provisions the defaults for ALL widgets on
// first install. Keep this file in lockstep with that function. Every frontend
// place that writes default widget settings must read from here so the
// storefront widgets get the SAME schema and values everywhere.
//
// Note: the product widget historically used an OLD field schema
// (to/Buy/and/get/…) that the storefront theme no longer reads — it reads
// `title/buyText/getText/…`. These constants use the correct (backend) schema.

export const PRODUCT_EMBED_DEFAULTS = {
  IsActive: true,
  title: 'Buy more, Save more',
  buyText: 'Buy',
  getText: 'Get',
  offText: '{{Discount}} Off',
  onEachText: 'on each',
  newPriceText: 'each item {{new-price}}',
  titleColor: '#000000',
  tableBgColor: '#FFFFFF',
  tableTextColor: '#000000',
  borderColor: '#D0D0D0',
  borderRadius: 8,
};

export const COUNTDOWN_DEFAULTS = {
  IsActive: true,
  containsProductTitle: true,
  afterProductTitleText: 'SALE ENDS IN',
  beforeProductTitleText: '',
  footerText: '',
  daysLabel: 'Days',
  hoursLabel: 'Hours',
  minutesLabel: 'Minutes',
  secondsLabel: 'Seconds',
  textColor: '#333333',
  digitColor: '#333333',
  digitBackgroundColor: '#f8f4f1',
  progressBarColor: '#45946D',
  progressBarBorderColor: '#808080',
  countDownType: 'Normal',
};

// Cart savings box labels (theme asset discount-cart.js reads the same keys and defaults).
export const CART_SAVINGS_LABEL_DEFAULTS = {
  giftsLabel: 'Free gifts',
  upgradeLabel: 'Upgrade savings',
  bundleLabel: 'Bundle savings',
  addonLabel: 'Add-on savings',
  discountsLabel: 'Discounts',
  saleLabel: 'Sale',
  savedLabel: 'Total savings',
};

// Product page placement of the PDP widgets (bundle / cross-sell / upsell / gift bar).
export const PDP_POSITION_DEFAULT = 'below_buy_buttons';
export const PDP_POSITION_OPTIONS = ['below_buy_buttons', 'above_buy_buttons'];

export const CART_EMBED_DEFAULTS = {
  IsActive: true,
  totalLabel: 'Subtotal',
  // Savings breakdown rows (discount-cart.js): only non-zero rows render; savedLabel is
  // the total row shown when more than one row is visible.
  ...CART_SAVINGS_LABEL_DEFAULTS,
  totalColor: '#000000',
  savedColor: '#45946D',
  savedBgColor: '#D9F0E4',
  boarderinSavedColor: '#45946D',
  borderRadius: 8,
  showBorder: true,
  align: 'left',
  lineThrough: 'true',
};

export const INVENTORY_EMBED_DEFAULTS = {
  IsActive: true,
  Position: 'below',
  StockLessNumber: 5,
  StockMinNumber: 1,
  StockMinEnabled: false,
  Text: '🔥 Only {live_stock_count} in stock',
  TextColor: '#8E1F0B',
  BackgroundColor: '#FEDAD9',
};

export const SALES_BADGE_DEFAULTS = {
  IsActive: true,
  salesBadgeText: 'Special Offer',
  salesBadgeTextColor: '#FFFFFF',
  salesBadgeBackgroundColor: '#FF0000',
  salesBadgeType: 'circle',
  salesBadgePosition: 'top_left',
  salesBadgeHasBorder: false,
};

export const SHIPPING_EMBED_DEFAULTS = {
  IsActive: true,
  progressMessage: 'Add {{amount}} more to unlock free shipping!',
  backgroundColor: '#FFFFFF',
  textColor: '#000000',
  progressColor: '#19A07F',
  progressBackground: '#D9D9D9',
  initialMessage: 'Add {{amount}} more to unlock {{discount}} off shipping!',
  inProgressMessage: "You're {{amount}} away from {{discount}} off shipping!",
  thresholdReachedMessage: "You've unlocked {{discount}} off shipping!",
  cardTextSize: 12,
  cardBorderColor: '#000000',
  cardBorderRadius: 8,
  progressBarBorderRadius: 4,
  showOnProductPage: true,
  showOnCartPage: true,
};

export const BUNDLE_TEXT_DEFAULTS = {
  SavePercentText: 'Save {value}% on the set',
  SaveAmountText: 'Save {value} on the set',
  SetPriceText: 'Set price {value}',
  CheapestFreeText: 'Cheapest item free',
  FreeGiftText: 'Free gift included',
  ChosenText: '{have} of {total} chosen',
  FreeText: 'Free',
  YouSaveText: 'You save {value}',
  AddedText: 'Added to cart',
  SoldOutText: 'Sold out',
  ThisItemText: 'This item',
  PickText: 'Choose one',
  OptionalText: 'optional',
  SeeAllText: 'See all ({count})',
  RetryText: 'Please try again',
  FromPriceText: 'From {price}',
  ChooseSlotHintText: 'Choose a {slot} to continue',
  SlotFallbackText: 'item',
  AllocCheapestText: 'The reward is applied to your cheapest {slot}',
  AllocDearestText: 'The reward is applied to your most expensive {slot}',
};

export const BUNDLE_EMBED_DEFAULTS = {
  IsActive: true,
  PdpPosition: PDP_POSITION_DEFAULT,
  // Storefront derives the palette from the theme while this is on; the colour
  // defaults below still apply once a merchant turns it off and picks their own.
  // Detection for shops saved before the flag existed: utils/widgetThemeColors.js.
  UseThemeColors: true,
  WidgetBackgroundColor: '#F7F7F7',
  HeaderBackgroundColor: '#CDFEE1',
  TitleColor: '#303030',
  SubTitleColor: '#616161',
  TextColor: '#303030',
  GiftBorderColor: '#A8D8FF',
  GiftBackgroundColor: '#EAF4FF',
  // Cart progress bar (Product Bundle phase 1, F1.10). Colours are the values
  // the backend's activateDefaultWidgets() provisions (kept in lockstep).
  ShowCartProgress: true,
  ProgressBarColor: '#45946D',
  ProgressBackgroundColor: '#D9F0E4',
  ProgressText: '{remaining} more to complete {title}',
  CompleteText: '{title}: set complete, discount applied',
  ShowToast: true,
  AddAllButtonText: 'Add set to cart',
  ButtonBackgroundColor: '#303030',
  ButtonTextColor: '#FFFFFF',
  // Storefront widget texts, same defaults as bundle-widget.js T(key, default).
  // DefaultTitle is left out on purpose: the widget picks 'Frequently bought together'
  // (FBT row) or 'Complete the set' depending on the layout when it is empty.
  ...BUNDLE_TEXT_DEFAULTS,
  // translations: { [locale]: { any text field above } }
  translations: {},
};

export const CROSS_SELL_DEFAULTS = {
  IsActive: true,
  PdpPosition: PDP_POSITION_DEFAULT,
  UseThemeColors: true,
  GeneralTitle: 'You may also like',
  AddToCardButton: 'Add',
  ViewCollectionButton: 'View collection',
  ViewVariantButton: 'View variants',
  ButtonBackgroundColor: '#29845A',
  ButtonTextColor: '#ffffff',
  WidgetBackgroundColor: '#F7F7F7',
  TextColor: '#303030',
};

export const BOGO_MODAL_DEFAULTS = {
  IsActive: true,
  headerTitle: 'Your current cart qualifies for these rewards',
  headerSubtitle: 'Now you are able to choose between them. Add before checking out!',
  selectButtonText: 'Select',
  addToCartButtonText: 'Add to Cart',
  continueShoppingButtonText: 'Continue Shopping',
  removeContinueShopping: false,
  badgeType: 'percentage',
  showFreeGiftBadge: true,
  primaryColor: '#000000',
  secondaryColor: '#FFFFFF',
  borderColor: '#C0C0C0',
  popupBackgroundColor: '#F4F4F4',
  headerBackgroundColor: '#616161',
  headerTextColor: '#FFFFFF',
  saleBadgeColor: '#3752B2',
  dismissBehavior: 'dismiss_until_triggered',
};

export const BOGO_FAB_DEFAULTS = {
  IsActive: true,
  removeCloseButton: false,
  position: 'right',
  borderRadius: 16,
  fabBackgroundColor: '#03A87C',
  fabIconColor: '#FFFFFF',
  closeButtonColor: '#FFFFFF',
  closeButtonIconColor: '#C0C0C0',
};

// Convenience map of every widget default, keyed by widget. Mirrors the full set
// the backend's activateDefaultWidgets() provisions on install.
export const DEFAULT_WIDGET_SETTINGS = {
  product: PRODUCT_EMBED_DEFAULTS,
  countdown: COUNTDOWN_DEFAULTS,
  cart: CART_EMBED_DEFAULTS,
  inventory: INVENTORY_EMBED_DEFAULTS,
  salesBadge: SALES_BADGE_DEFAULTS,
  shipping: SHIPPING_EMBED_DEFAULTS,
  bundle: BUNDLE_EMBED_DEFAULTS,
  crossSell: CROSS_SELL_DEFAULTS,
  bogoModal: BOGO_MODAL_DEFAULTS,
  bogoFab: BOGO_FAB_DEFAULTS,
};
